
import { useState, useEffect, useRef } from "react";
import './ServicesSubPages.css';

const serviceData = {
  "service-software": {
    icon: "",
    title: "Software Services",
    badge: "",
    tagline: "Enterprise-grade software solutions for utilities, telecoms, and infrastructure-led organisations.",
    overview: (
      <>
        At Inovaantage, we design, build, secure, and modernize
        enterprise-grade software solutions that power mission-critical operations for utilities,
        telecoms, and infrastructure-led organizations worldwide.

        <br />
        <br />

        Combining deep domain expertise with a strong engineering foundation, we help your
        organization reduce risk, accelerate digital transformation, and maximize ROI from your
        technology investments.

        <br />
        <br />
        Our Promise for Complex Environments: Our software services are purpose-built for
        high-stakes infrastructure where reliability, scalability, regulatory compliance, and data
        integrity are absolute, non-negotiable requirements.
      </>),
    capabilities: [
      {
        icon: "⚙️",
        title: "Custom Software Development",
        desc: "Design and development of tailor-made software applications engineered to meet specific business, operational, and regulatory requirements in complex enterprise environments.",
        whatWeDo: [
          "Design and build enterprise-grade applications using modern, secure, and scalable architectures",
          "Develop GIS-centric and data-intensive platforms aligned to real-world operational workflows",
          "Modernise and re-engineer legacy applications without disrupting business operations",
          "Build API-first and microservices-based solutions for seamless system integration",
        ],
        whyDifferent: [
          "Deep domain-led engineering — we understand utilities and telecom operations, not just technology",
          "Focus on long-term maintainability, not short-term delivery",
          "Software designed for mission-critical reliability, not generic use cases",
        ],
        valueDelivered: "Software that fits your business, scales with growth, and stands the test of time.",
      },
      {
        icon: "🚀",
        title: "Digital Transformation & Modernisation",
        desc: "End-to-end transformation of legacy systems into modern, agile, and cloud-ready platforms that improve efficiency, scalability, and business agility.",
        whatWeDo: [
          "Migrate legacy platforms to modern, cloud-native or hybrid architectures",
          "Transform monolithic applications into modular, microservices-based systems",
          "Enable DevOps, CI/CD, and automation to accelerate delivery cycles",
          "Optimise business processes through intelligent automation",
        ],
        whyDifferent: [
          "Transformation driven by business continuity, not just technology refresh",
          "Proven expertise handling high-risk, large-scale legacy estates",
          "Structured approach that minimises downtime and operational risk",
        ],
        valueDelivered: "Faster innovation, reduced technical debt, and future-ready digital platforms.",
      },
      {
        icon: "🗺️",
        title: "GIS & Utility Network Services",
        desc: "Specialist GIS and network services focused on enabling accurate, high-performance spatial systems for utilities and telecom organisations.",
        whatWeDo: [
          "Implement and configure ESRI Utility Network and GIS platforms",
          "Migrate Geometric Network data to Utility Network with high accuracy",
          "Design and optimise asset, conduit, and network data models",
          "Integrate GIS with enterprise systems and operational platforms",
        ],
        whyDifferent: [
          "Recognised specialists in ESRI Utility Network migrations",
          "Proven accelerators and proprietary tooling for complex network data",
          "Practical experience delivering for large, regulated utilities globally",
        ],
        valueDelivered: "Trusted, regulation-ready GIS systems that support operational and planning decisions.",
      },
      {
        icon: "🔄",
        title: "Data Migration, Integration & ETL Services",
        desc: "Secure, automated, and highly accurate movement and integration of data across systems, ensuring consistency, quality, and business continuity.",
        whatWeDo: [
          "Design and execute large-scale, multi-system data migrations",
          "Build automated ETL pipelines using FME and custom frameworks",
          "Validate, cleanse, and reconcile data to ensure accuracy",
          "Enable near-real-time data synchronisation across platforms",
        ],
        whyDifferent: [
          "Automation-first approach delivering near-zero data loss",
          "Repeatable, proven migration frameworks — not manual scripts",
          "Extensive experience migrating mission-critical utility data",
        ],
        valueDelivered: "Confidence in data integrity, faster migrations, and reduced manual effort.",
      },
      {
        icon: "🛡️",
        title: "Cybersecurity & Application Security",
        desc: "Comprehensive security services embedded across the software lifecycle to protect applications, data, and infrastructure from evolving cyber threats.",
        whatWeDo: [
          "Design secure application and system architectures",
          "Perform application security reviews and penetration testing",
          "Embed security into CI/CD pipelines (DevSecOps)",
          "Support regulatory, audit, and compliance requirements",
        ],
        whyDifferent: [
          "CREST-aligned penetration testing capability",
          "Security expertise combined with deep application knowledge",
          "Practical, actionable security improvements — not theoretical reports",
        ],
        valueDelivered: "Reduced cyber risk, stronger compliance, and resilient digital systems.",
      },
      {
        icon: "☁️",
        title: "Software Upgrade & Cutover Assurance",
        desc: "Structured planning and execution of software upgrades and system cutovers to ensure minimal downtime and zero disruption to critical operations.",
        whatWeDo: [
          "Define upgrade strategies and cutover roadmaps",
          "Execute parallel runs, rehearsals, and rollback planning",
          "Coordinate technical and business readiness activities",
          "Validate systems post-upgrade to ensure stability",
        ],
        whyDifferent: [
          "Specialists in high-risk upgrades where failure is not an option",
          "Business-first cutover planning, not purely technical execution",
          "Strong track record of zero-downtime and low-impact transitions",
        ],
        valueDelivered: "Predictable upgrades, protected operations, and business confidence.",
      },
    ],
    outcomes: [
      { val: "60%", label: "Reduction in Manual Processes" },
      { val: "99.9%", label: "System Uptime SLA" },
      { val: "3×", label: "Faster Time-to-Insight" },
      { val: "40%", label: "Cost Savings vs Off-Shelf" },
    ],
    clients: ["Utilities", "Telecoms", "Infrastructure Operators", "Critical Enterprises"],
  },

  "service-implementation": {
    icon: "",
    title: "COTS Implementation Services",
    tagline: "Enterprise-grade implementation of mission-critical commercial platforms – delivered with confidence, control, and continuity.",
    overview: (
      <>
        <b>Enterprise-grade implementation of mission-critical commercial platforms – delivered with confidence, control, and continuity</b><br /><br />
        At  <b>Inovaantage</b>, we specialise in the implementation, integration, and assurance of <b>Commercial Off-The-Shelf (COTS)</b> platforms used by utilities, telecoms, and digital infrastructure operators. Our focus is not just on deploying software, but on ensuring that complex COTS systems are <b>configured correctly, integrated seamlessly, adopted successfully, and operate reliably in live environments.</b>

        <br />
        <br />

        We bring deep domain expertise across utilities and telecom operations, enabling us to bridge the gap between vendor software and real-world operational needs.

      </>),
    capabilities: [
      {
        icon: "👷",
        title: "Workforce Management (WFM) Systems",
        desc: "Implementation and optimisation of workforce management platforms that support planning, scheduling, dispatch, and field operations.",
        whatWeDo: [
          "End-to-end WFM system implementation and configuration",
          "Business process alignment for field and operational teams",
          "Integration with GIS, ERP, asset, and mobile platforms",
          "Change management and user adoption support",
        ],
        whyDifferent: [
          "WFM designed around utility and telecom field realities, not generic workflows",
          "Strong focus on adoption, productivity, and operational outcomes",
          "Experience supporting distributed and hybrid workforce models",
        ],
        valueDelivered: "Improved productivity, better field utilisation, and higher service reliability.",
      },
      {
        icon: "📡",
        title: "Operations Support Systems (OSS)",
        desc: "Implementation and integration of OSS platforms to manage networks, services, and operational performance.",
        whatWeDo: [
          "OSS configuration for network monitoring, fault, and performance management",
          "Service provisioning and activation workflows",
          "Integration with BSS, GIS, inventory, and network platforms",
          "Operational dashboards and analytics",
        ],
        whyDifferent: [
          "Deep understanding of telecom and utility network operations",
          "Strong integration capability across complex IT/OT landscapes",
          "Focus on automation and operational efficiency",
        ],
        valueDelivered: "Improved network visibility, faster service rollout, and reduced operational cost.",
      },
      {
        icon: "💳",
        title: "Business Support Systems (BSS)",
        desc: "Implementation of billing, revenue, and customer management platforms that support modern digital services.",
        whatWeDo: [
          "Billing and charging system implementation",
          "Subscription and product lifecycle configuration",
          "Revenue assurance and compliance enablement",
          "Integration with OSS, CRM, and analytics platforms",
        ],
        whyDifferent: [
          "Experience handling complex pricing and billing models",
          "Strong emphasis on accuracy, auditability, and regulatory compliance",
          "End-to-end view from service activation to revenue realisation",
        ],
        valueDelivered: "Reduced revenue leakage, improved billing accuracy, and enhanced customer trust.",
      },
      {
        icon: "⚡",
        title: "SCADA Systems",
        desc: "Secure and resilient implementation of SCADA platforms for real-time monitoring and control of critical infrastructure.",
        whatWeDo: [
          "SCADA system deployment and configuration",
          "Integration with OT, IT, and enterprise platforms",
          "Real-time monitoring, alarms, and visualisation",
          "Security hardening and compliance support",
        ],
        whyDifferent: [
          "Strong experience in mission-critical, regulated environments",
          "Security-first approach aligned to modern cyber threats",
          "Seamless integration with ADMS, GIS, and enterprise systems",
        ],
        valueDelivered: "Increased reliability, improved operational control, and reduced downtime.",
      },
      {
        icon: "🔌",
        title: "Advanced Distribution Management Systems (ADMS)",
        desc: "Implementation of ADMS platforms to enable smarter, more resilient distribution network operations.",
        whatWeDo: [
          "ADMS configuration for real-time monitoring and control",
          "Integration with SCADA, DERMS, GIS, and outage systems",
          "Analytics-driven optimisation and fault management",
          "Operator enablement and training",
        ],
        whyDifferent: [
          "ADMS implementations grounded in real distribution operations",
          "Strong focus on reliability, resilience, and automation",
          "Proven integration expertise across grid ecosystems",
        ],
        valueDelivered: "Improved grid reliability, faster restoration, and optimised network performance.",
      },
      {
        icon: "📊",
        title: "Advanced Metering Infrastructure (AMI)",
        desc: "Implementation and integration of AMI platforms to support smart metering and data-driven energy services.",
        whatWeDo: [
          "AMI system deployment and data integration",
          "Secure meter data management and analytics",
          "Integration with billing, analytics, and customer platforms",
          "Compliance, privacy, and cybersecurity assurance",
        ],
        whyDifferent: [
          "Strong focus on data quality, security, and scalability",
          "End-to-end understanding from meter to customer insight",
          "Experience operating in highly regulated environments",
        ],
        valueDelivered: "Better energy management, improved customer engagement, and operational efficiency.",
      },
    ],
    outcomes: [
      { val: "95%", label: "Successful Implementation Rate" },
      { val: "99.9%", label: "System Availability" },
      { val: "40%", label: "Faster Operational Efficiency" },
      { val: "24/7", label: "Enterprise Support Coverage" },
    ],
    clients: ["Utilities", "Telecoms", "Digital Infrastructure", "Critical Infrastructure"],
  },

  "service-geospatial": {
    icon: "",
    title: "Geospatial Services",
    tagline: "Precision spatial intelligence solutions for utilities, telecoms, and infrastructure operators.",
    overview: (
      <>
        <b>Modern, mission-critical geospatial solutions for utilities, telecoms, and infrastructure operators</b>
        <br /><br />
        At <b>Inovaantage</b>, our Geospatial Services are built for today’s realities: cloud-first platforms, Utility Network–centric GIS, data-driven decision-making, regulatory pressure, and zero tolerance for operational disruption. We help organisations modernise, manage, and monetise their spatial data by combining deep domain expertise with proven delivery accelerators.

        <br />
        <br />

        Our services go beyond traditional GIS implementation — we focus on <b>outcomes, resilience, and long-term value.</b>

      </>),
    capabilities: [
      {
        icon: "🌍",
        title: "ArcGIS Pro & Utility Network Migration",
        desc: "Structured, low-risk migration from legacy GIS and Geometric Network to Esri ArcGIS Pro and Utility Network.",
        whatWeDo: [
          "End-to-end migration strategy aligned to business and regulatory needs",
          "Automated migration of assets, connectivity, associations, and rules",
          "Utility Network model configuration and optimisation",
          "Cutover planning, parallel runs, and post-migration validation",
          "User enablement and adoption support",
        ],
        whyDifferent: [
          "Specialists in high-complexity Utility Network migrations, not generic GIS upgrades",
          "Proven automation frameworks delivering near-zero data loss",
          "Deep understanding of utility operations, not just Esri tooling",
        ],
        valueDelivered: "Faster migrations, reduced operational risk, and future-ready GIS platforms.",
      },
      {
        icon: "🔄",
        title: "Geospatial Data Management",
        desc: "Enterprise-grade management of spatial data to ensure accuracy, usability, and long-term scalability.",
        whatWeDo: [
          "Multi-source spatial data acquisition and integration",
          "Data quality assessment, cleansing, and governance",
          "Utility-grade geodatabase design and optimisation",
          "Metadata, lineage, and compliance-ready data management",
          "Secure, scalable data architectures",
        ],
        whyDifferent: [
          "Data management designed for regulated, mission-critical environments",
          "Strong focus on automation, governance, and auditability",
          "Practical experience managing large, complex utility datasets",
        ],
        valueDelivered: "Trusted data, improved decision-making, and reduced data risk.",
      },
      {
        icon: "📡",
        title: "Survey & Field Data Capture",
        desc: "High-accuracy field data capture using modern survey and digital data collection techniques.",
        whatWeDo: [
          "Utility and asset surveys tailored to operational needs",
          "GNSS, LiDAR, UAV, and mobile data capture",
          "Digital field workflows and validation rules",
          "QA/QC aligned to enterprise GIS standards",
          "Seamless integration into Utility Network and GIS platforms",
        ],
        whyDifferent: [
          "Survey designed for direct system ingestion, not manual rework",
          "Strong alignment between field data and enterprise GIS models",
          "Focus on accuracy, consistency, and downstream usability",
        ],
        valueDelivered: "Reliable field data, reduced rework, and faster asset onboarding.",
      },
      {
        icon: "🗂️",
        title: "GIS Development & System Implementation",
        desc: "Design and delivery of custom GIS solutions aligned to real operational workflows.",
        whatWeDo: [
          "Custom GIS application development (web, desktop, mobile)",
          "Utility-specific GIS workflows and extensions",
          "System integration with ERP, ADMS, OMS, and asset systems",
          "Performance tuning for large-scale spatial datasets",
        ],
        whyDifferent: [
          "GIS solutions built around how utilities actually operate",
          "Strong focus on performance, security, and maintainability",
          "Delivery teams with both GIS and enterprise software expertise",
        ],
        valueDelivered: "GIS platforms that users adopt, trust, and rely on daily.",
      },
      {
        icon: "📊",
        title: "Advanced Geospatial Analytics",
        desc: "Advanced spatial analytics that convert location data into operational and strategic insight.",
        whatWeDo: [
          "Network and asset analytics",
          "Spatial modelling and optimisation",
          "Risk, impact, and scenario analysis",
          "Predictive analytics using spatial intelligence",
        ],
        whyDifferent: [
          "Analytics grounded in operational context, not academic models",
          "Designed to support planning, investment, and regulatory decisions",
          "Scalable analytics for enterprise environments",
        ],
        valueDelivered: "Better planning, reduced risk, and smarter investment decisions.",
      },
      {
        icon: "☁️",
        title: "Geospatial Visualisation & Dashboards",
        desc: "Intuitive, decision-focused visualisation of complex spatial data.",
        whatWeDo: [
          "Interactive enterprise maps and web GIS portals",
          "Executive dashboards combining spatial and business data",
          "2D and 3D visualisation for networks and assets",
          "Role-based views for operations, planning, and leadership",
        ],
        whyDifferent: [
          "Visualisation designed for decision-makers, not just GIS users",
          "Performance-optimised for large, live datasets",
          "Clean, usable designs that drive adoption",
        ],
        valueDelivered: "Faster insight, better communication, and confident decisions.",
      },
    ],
    outcomes: [
      { val: "50M+", label: "Assets Digitised" },
      { val: "99.9%", label: "Spatial Data Accuracy" },
      { val: "15+", label: "Countries Supported" },
      { val: "24/7", label: "GIS Operational Support" },
    ],
    clients: ["Utilities", "Telecoms", "Infrastructure Operators", "Government Agencies"],
  },

  "service-managed": {
    icon: "",
    title: "Managed Services",
    tagline: "Reliable, scalable, and proactive support services for mission-critical enterprise platforms.",
    overview: (
      <>
        <b>Operate with confidence. Scale with control. Focus on what matters most.</b>
        <br />
        In today’s always-on, digitally regulated environment, organisations need more than IT support — they need trusted operational partners. Inovaantage’s Managed Services are designed to take ownership of complex IT, data, and platform operations, ensuring stability, security, performance, and continuous optimisation.


        <br />
        <br />

        We don’t just manage systems — we assure outcomes. From cloud and DevOps to service desk, data platforms, and end-to-end technology ecosystems, our managed services allow you to focus on core business objectives while we ensure your digital backbone runs reliably, securely, and efficiently.


      </>),
    capabilities: [
      {
        icon: "☁️",
        title: "Cloud Managed Services",
        desc: (
          <>
            <b>Secure, scalable, and cost-optimised cloud operations — managed end to end.</b>
            <br />
            Inovaantage provides comprehensive Cloud Managed Services that remove the operational burden of complex cloud environments while maximising performance, resilience, and value.
          </>),
        whatWeDo: [
          "Cloud migration and deployment with minimal disruption across leading cloud platforms",
          "24x7 monitoring and proactive optimisation for availability, performance, and cost efficiency",
          "Security, governance, and compliance aligned to regulatory and enterprise standards",
          "Disaster recovery and business continuity planning with rapid recovery capabilities",
          "Cloud cost management (FinOps) with transparent reporting and optimisation strategies",
        ],
        whyDifferent: [
          "Built for mission-critical and regulated environments, not generic IT workloads",
          "Automation-led monitoring and remediation, reducing risk and downtime",
          "Strong experience across utilities, telecoms, and infrastructure platforms",
        ],
        valueDelivered: "Lower cloud risk, predictable costs, improved resilience, and confident scale.",
      },
      {
        icon: "🔄",
        title: "DevOps Managed Services",
        desc: (
          <>
            <b>Accelerating delivery without compromising stability or security.
            </b>
            <br />
            Our DevOps Managed Services help organisations modernise software delivery while maintaining operational control, security, and quality.

          </>),
        whatWeDo: [
          "CI/CD pipeline design and management for faster, higher-quality releases",
          "Infrastructure as Code (IaC) for consistent, repeatable, and scalable environments",
          "Monitoring, logging, and observability for real-time visibility",
          "Containerisation and orchestration using Docker, Kubernetes, and cloud-native platforms",
          "DevOps operating models aligned to Agile and enterprise governance",
        ],
        whyDifferent: [
          "DevOps designed for enterprise-scale, regulated systems, not start-up experimentation",
          "Strong focus on security, auditability, and operational assurance",
          "Seamless integration with legacy platforms and modern cloud stacks",
        ],
        valueDelivered: "Faster time-to-market, improved release stability, and higher engineering productivity.",
      },
      {
        icon: "🖥️",
        title: "Service Desk Managed Services",
        desc: (
          <>
            <b>Reliable, responsive, and user-centric IT support.
            </b>
            <br />
            Inovaantage’s Service Desk Managed Services provide a single, accountable point of contact for IT support, ensuring high service quality and consistent user experience.

          </>),
        whatWeDo: [
          "24x7 multi-channel support (phone, email, chat, self-service)",
          "Incident and problem management with structured prioritisation and root-cause analysis",
          "Service request fulfilment for standard IT and access needs",
          "Knowledge management to enable faster resolution and user self-service",
          "Performance reporting and continuous improvement through analytics",
        ],
        whyDifferent: [
          "Designed for enterprise and operational environments, not basic helpdesk support",
          "Strong SLA governance and service transparency",
          "Focus on user productivity and business continuity",
        ],
        valueDelivered: "Reduced downtime, improved user satisfaction, and predictable IT operations.",
      },
      {
        icon: "🔒",
        title: "End-to-End (E2E) Managed Services",
        desc: (
          <>
            <b>One partner. One operating model. Complete accountability.
            </b>
            <br />
            Our End-to-End Managed Services provide holistic ownership of technology ecosystems — spanning infrastructure, applications, data, security, and operations.
          </>),
        whatWeDo: [
          "IT strategy and consulting aligned to long-term business objectives",
          "Infrastructure and platform management across on-prem, cloud, and hybrid environments",
          "Application lifecycle management from build to run",
          "Cybersecurity and compliance management for evolving threat landscapes",
          "Continuous performance and cost optimisation across the technology stack",
        ],
        whyDifferent: [
          "True end-to-end accountability, not fragmented vendor management",
          "Deep integration across IT, OT, GIS, and data platforms",
          "Designed for business continuity, resilience, and scale",
        ],
        valueDelivered: "Simplified operations, reduced risk, and technology that supports growth.",
      },
      {
        icon: "📊",
        title: "Big Data & Analytics Management",
        desc: (
          <>
            <b>Empowering Businesses with Data-Driven Confidence.
            </b>
            <br />
            Inovaantage’s Big Data Management Managed Services help organisations unlock the full value of their data while ensuring governance, quality, and scalability. We manage complex data ecosystems so your teams can focus on insight, innovation, and outcomes.
          </>),
        whatWeDo: [
          "Design and implementation of enterprise-grade data lakes",
          "Real-time and batch data ingestion pipelines",
          "Data cleansing, standardisation, and validation",
          "Metadata enrichment and contextual augmentation",
          "Advanced analytics platforms leveraging AI and machine learning",
        ],
        whyDifferent: [
          "Data lakes designed for long-term governance and performance, not experimentation",
          "Strong integration with cloud, GIS, and enterprise systems",
          "Proven methods for regulated and operational datasets",
        ],
        valueDelivered: "Improved decision-making through trusted, well-managed data.",
      },
      {
        icon: "📡",
        title: "Data Enrichment & Quality Management",
        desc: (
          <>
            <b>Turning raw data into trusted, decision-ready information
            </b>
            <br />
            Data Enrichment & Quality Management helps organizations transform raw and inconsistent data into accurate, reliable, and decision-ready information through cleansing, validation, metadata enrichment, and automated quality monitoring. Inovaantage ensures trusted, business-ready datasets that improve operational efficiency, governance, and decision-making.
          </>),
        whatWeDo: [
          "Data cleansing, standardisation, and validation",
          "Metadata enrichment and contextual augmentation",
          "Master and reference data alignment",
          "Automated data quality monitoring",
        ],
        whyDifferent: [
          "Focus on business usability and trust, not just data volume",
          "Proven methods for regulated and operational datasets",
        ],
        valueDelivered: "Stable GIS operations, predictable costs, and continuous improvement.",
      },
    ],
    outcomes: [
      { val: "24/7", label: "Operational Support" },
      { val: "99.95%", label: "Platform Availability" },
      { val: "100%", label: "SLA Compliance" },
      { val: "30%", label: "Reduced IT Overhead" },
    ],
    clients: ["Utilities", "Telecoms", "Infrastructure Operators", "Enterprise Organisations"],
  },
};

/* ─── CSS (injected once) ─── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

  /* PAGE */
  .ssp-root { padding-top: 70px; background: #FFFFFF; min-height: 100vh; }

  /* ── HERO ── */
  .ssp-hero {
    position: relative; overflow: hidden;
    padding: 88px 48px 52px;
    background:
      radial-gradient(ellipse at 20% 50%, rgba(232,96,28,0.08) 0%, transparent 55%),
      linear-gradient(135deg, #FFF5F0 0%, #FFFFFF 60%, #FFF8F5 100%);
    border-bottom: 1px solid rgba(232,96,28,0.10);
    text-align: center;
  }
  .ssp-hero-grid {
    position: absolute; inset: 0; pointer-events: none;
    background-image:
      linear-gradient(rgba(249,115,22,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(249,115,22,0.04) 1px, transparent 1px);
    background-size: 48px 48px;
  }
  .ssp-hero-badge {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(249,115,22,0.08);
    border: 1px solid rgba(249,115,22,0.22);
    color: var(--accent-cyan, #F97316);
    font-size: 0.72rem; font-weight: 700;
    letter-spacing: 1.5px; text-transform: uppercase;
    padding: 6px 16px; border-radius: 100px;
    margin-bottom: 24px; position: relative;
  }
  .ssp-hero-title {
    font-family: 'Syne', sans-serif;
    font-size: clamp(2rem, 5vw, 3.5rem);
    font-weight: 800; line-height: 1.1;
    color: #0a1929; margin-bottom: 18px; position: relative;
  }
  .ssp-hero-title .accent-word {
    background: rgb(249,66,0);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }
  .ssp-hero-tagline {
    font-size: 1.05rem; color: #0a1929;
    max-width: 640px; margin: 0 auto; line-height: 1.75; position: relative;
  }

  /* ── SECTIONS ── */
  .ssp-section { padding: 80px 0; }
  .ssp-section.alt { background: rgba(245, 241, 237, 0.78); }
  .ssp-container { max-width: 1180px; margin: 0 auto; padding: 0 48px; }
  .ssp-section-title {
    font-family: 'Syne', sans-serif;
    font-size: clamp(1.5rem, 3vw, 2rem); font-weight: 800; color: #0a1929;
    margin-bottom: 48px;
    display: flex; align-items: center; gap: 16px;
  }
  .ssp-section-title::after {
    content: ''; flex: 1; height: 1px;
    background: linear-gradient(90deg, rgba(249,115,22,0.35), transparent);
  }

  /* ── OVERVIEW ── */
  .ssp-overview-card {
    background: rgba(249,115,22,0.04);
    border: 1px solid rgba(249,115,22,0.14);
    border-radius: 20px; padding: 40px 48px;
    position: relative; overflow: hidden;
  }
  .ssp-overview-card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, #F97316, #FF8C00, transparent);
  }
  .ssp-overview-card h2 {
    font-family: 'Syne', sans-serif; font-size: 1.35rem; font-weight: 700;
    color: rgb(249,66,0); margin-bottom: 14px;
  }
  .ssp-overview-card p { font-size: 1rem; color: #0a1929; line-height: 1.85; }

  /* ── CAPABILITY CARDS ── */
  .ssp-cap-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }

  .cap-card {
    position: relative; background: white;
    border: 1px solid rgba(0,212,255,0.12); 
    border-radius: 20px;
    border-color:rgba(232,96,28,0.12);
    box-shadow:0 2px 12px rgba(0,0,0,0.12);
    padding: 30px 26px 26px;
    display: flex; flex-direction: column;
    transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
    overflow: hidden; cursor: default;
  }
  .cap-card::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(249,115,22,0.05) 0%, transparent 60%);
    pointer-events: none;
  }
  .cap-card:hover {
    transform: translateY(-6px);
    border-color: rgba(249,115,22,0.4);
    box-shadow: 0 20px 56px rgba(249,115,22,0.10), 0 0 0 1px rgba(249,115,22,0.06);
  }
  .cap-num {
    position: absolute; top: 18px; right: 18px;
    font-family: 'JetBrains Mono', monospace; font-size: 0.62rem; font-weight: 600;
    color: rgba(249,115,22,0.3); letter-spacing: 1px;
  }
  .cap-icon-box {
    width: 54px; height: 54px;
    background: linear-gradient(135deg, rgba(249,115,22,0.15), rgba(255,140,0,0.08));
    border: 1px solid rgba(249,115,22,0.22); border-radius: 16px;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.55rem; margin-bottom: 16px;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  .cap-card:hover .cap-icon-box { transform: scale(1.08) rotate(-3deg); box-shadow: 0 8px 24px rgba(249,115,22,0.2); }
  .cap-accent-bar {
    height: 2px; margin-bottom: 16px; border-radius: 2px;
    background: linear-gradient(90deg, rgba(249,115,22,0.6), rgba(255,140,0,0.2));
    transform-origin: left; transform: scaleX(0);
    transition: transform 0.5s cubic-bezier(0.16,1,0.3,1);
  }
  .cap-card:hover .cap-accent-bar { transform: scaleX(1); }
  .cap-title {
    font-family: 'Syne', sans-serif; font-size: 1rem; font-weight: 700;
    color: #0a1929; margin-bottom: 10px; line-height: 1.3;
  }
  .cap-desc { font-size: 0.87rem; color: #0a1929; line-height: 1.75; flex: 1; margin-bottom: 22px; }
  .cap-readmore {
    display: inline-flex; align-items: center; gap: 8px;
    background: transparent; border: 1.5px solid rgba(249,115,22,0.35);
    color: var(--accent-cyan, #F97316);
    font-family: 'DM Sans', sans-serif; font-size: 0.84rem; font-weight: 600;
    padding: 9px 20px; border-radius: 100px; cursor: pointer;
    transition: all 0.25s ease; align-self: flex-start; letter-spacing: 0.3px;
  }
  .cap-readmore:hover { background: rgb(249,66,0);color:white; border-color: var(--accent-cyan, #F97316); transform: translateX(4px); }
  .cap-arrow { display: inline-block; transition: transform 0.25s ease; }
  .cap-readmore:hover .cap-arrow { transform: translateX(4px); }

  /* ── OUTCOMES ── */
  .ssp-outcomes-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
  .ssp-outcome-card {
    background: rgba(249,66,0,0.149);
    border-radius: 18px; padding: 32px 24px; text-align: center;
    transition: transform 0.3s ease, border-color 0.3s ease;
    position: relative; overflow: hidden;
  }
  .ssp-outcome-card::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, transparent, #F97316, transparent);
  }
  .ssp-outcome-card:hover { transform: translateY(-4px); border-color: rgba(249,115,22,0.35); }
  .ssp-outcome-val {
    display: block; font-family: 'Syne', sans-serif;
    font-size: clamp(1.8rem, 3vw, 2.0rem); font-weight: 800;
    background: rgb(249,66,0);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
    line-height: 1.1; margin-bottom: 8px;
  }
  .ssp-outcome-label { display: block; font-size: 0.82rem; color: #0a1929; font-weight: 500; line-height: 1.4; }

  /* ── CLIENTS ── */
  .ssp-clients { display: flex; flex-wrap: wrap; gap: 12px; }
  .ssp-client-badge {
    background: rgba(249,115,22,0.07); border: 1px solid rgba(249,115,22,0.18);
    color: var(--accent-cyan, #F97316); font-size: 0.88rem; font-weight: 600;
    padding: 10px 22px; border-radius: 100px; letter-spacing: 0.3px;
    transition: background 0.2s, transform 0.2s;
  }
  .ssp-client-badge:hover { background: rgba(249,115,22,0.14); transform: translateY(-2px); }

  /* ── DETAIL PANEL ── */
  .dp-backdrop {
    position: fixed; inset: 0; z-index: 2000;
    background: rgba(4, 10, 22, 0.75);
    backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    display: flex; align-items: flex-end;
    animation: dp-fade 0.28s ease forwards;
  }
  @keyframes dp-fade { from{opacity:0} to{opacity:1} }

  .dp-panel {
    width: 100%; max-height: 90vh;
    background: #07111f;
    border-top: 1px solid rgba(249,115,22,0.28);
    border-radius: 28px 28px 0 0;
    overflow-y: auto;
    transform: translateY(100%);
    transition: transform 0.42s cubic-bezier(0.16,1,0.3,1);
    scrollbar-width: thin; scrollbar-color: rgba(249,115,22,0.3) transparent;
  }
  .dp-panel::-webkit-scrollbar { width: 4px; }
  .dp-panel::-webkit-scrollbar-thumb { background: rgba(249,115,22,0.35); border-radius: 4px; }
  .dp-panel.dp-open { transform: translateY(0); }

  .dp-handle { width: 48px; height: 5px; background: rgba(255,255,255,0.11); border-radius: 100px; margin: 16px auto 0; }

  .dp-inner { max-width: 980px; margin: 0 auto; padding: 40px 48px 80px; }

  .dp-header {
    display: flex; align-items: flex-start; justify-content: space-between;
    gap: 24px; margin-bottom: 36px; padding-bottom: 28px;
    border-bottom: 1px solid rgba(249,115,22,0.12);
  }
  .dp-header-left { display: flex; align-items: flex-start; gap: 18px; }
  .dp-icon-wrap {
    width: 62px; height: 62px; flex-shrink: 0;
    background: linear-gradient(135deg, rgba(249,115,22,0.18), rgba(255,140,0,0.08));
    border: 1px solid rgba(249,115,22,0.28); border-radius: 18px;
    display: flex; align-items: center; justify-content: center; font-size: 1.7rem;
    box-shadow: 0 0 32px rgba(249,115,22,0.12);
  }
  .dp-stag {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(249,115,22,0.08); border: 1px solid rgba(249,115,22,0.2);
    color: var(--accent-cyan, #F97316); font-size: 0.68rem; font-weight: 700;
    letter-spacing: 1.2px; text-transform: uppercase;
    padding: 3px 10px; border-radius: 100px; margin-bottom: 6px;
    display: block;
  }
  .dp-title {
    font-family: 'Syne', sans-serif;
    font-size: clamp(1.3rem, 3vw, 1.9rem); font-weight: 800; color: #f0f6ff; line-height: 1.2;
  }
  .dp-close {
    flex-shrink: 0; width: 42px; height: 42px;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px; color: #8aabcf; font-size: 1.1rem;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: all 0.2s ease;
  }
  .dp-close:hover { background: rgba(249,115,22,0.12); border-color: rgba(249,115,22,0.3); color: #F97316; transform: rotate(90deg); }

  .dp-desc {
    font-size: 1.02rem; color: #8aabcf; line-height: 1.8; margin-bottom: 44px;
    padding: 18px 22px; background: rgba(249,115,22,0.04);
    border-left: 3px solid rgba(249,115,22,0.4); border-radius: 0 12px 12px 0;
  }

  .dp-body { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; margin-bottom: 36px; }

  .dp-col-label {
    font-family: 'Syne', sans-serif; font-size: 0.68rem; font-weight: 700;
    letter-spacing: 1.8px; text-transform: uppercase; color: var(--accent-cyan, #F97316);
    margin-bottom: 14px; display: flex; align-items: center; gap: 10px;
  }
  .dp-col-label::after { content: ''; flex: 1; height: 1px; background: linear-gradient(90deg, rgba(249,115,22,0.3), transparent); }

  .dp-item {
    display: flex; align-items: flex-start; gap: 10px;
    font-size: 0.88rem; color: #8aabcf; line-height: 1.65;
    padding: 10px 13px; border-radius: 10px;
    background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04);
    margin-bottom: 8px; transition: border-color 0.2s, background 0.2s;
  }
  .dp-item:hover { background: rgba(249,115,22,0.05); border-color: rgba(249,115,22,0.15); color: #c5daf0; }
  .dp-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--accent-cyan, #F97316); flex-shrink: 0; margin-top: 7px; }

  .dp-diff {
    display: flex; align-items: flex-start; gap: 10px;
    font-size: 0.88rem; color: #8aabcf; line-height: 1.65;
    padding: 10px 13px; border-radius: 10px;
    background: rgba(249,115,22,0.04); border: 1px solid rgba(249,115,22,0.10);
    margin-bottom: 8px; transition: border-color 0.2s, background 0.2s;
  }
  .dp-diff:hover { background: rgba(249,115,22,0.09); border-color: rgba(249,115,22,0.25); color: #c5daf0; }
  .dp-star { color: var(--accent-cyan, #F97316); font-size: 0.82rem; flex-shrink: 0; margin-top: 3px; }

  .dp-value {
    display: flex; align-items: flex-start; gap: 16px;
    background: linear-gradient(135deg, rgba(249,115,22,0.10), rgba(255,140,0,0.05));
    border: 1px solid rgba(249,115,22,0.22); border-radius: 16px; padding: 20px 24px;
  }
  .dp-val-icon {
    width: 40px; height: 40px; flex-shrink: 0;
    background: rgba(249,115,22,0.15); border-radius: 10px;
    display: flex; align-items: center; justify-content: center; font-size: 1.1rem;
  }
  .dp-val-lbl {
    font-family: 'Syne', sans-serif; font-size: 0.62rem; font-weight: 700;
    letter-spacing: 1.5px; text-transform: uppercase; color: rgba(249,115,22,0.65); margin-bottom: 4px;
  }
  .dp-val-txt { font-size: 0.97rem; color: #e0edf8; line-height: 1.6; font-weight: 500; }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .ssp-cap-grid { grid-template-columns: repeat(2, 1fr); }
    .ssp-outcomes-grid { grid-template-columns: repeat(2, 1fr); }
    .ssp-hero { padding: 80px 32px 60px; }
    .ssp-container { padding: 0 32px; }
    .ssp-section { padding: 60px 0; }
  }
  @media (max-width: 768px) {
    .ssp-hero { padding: 80px 20px 48px; }
    .ssp-container { padding: 0 20px; }
    .ssp-section { padding: 48px 0; }
    .ssp-cap-grid { grid-template-columns: 1fr; gap: 16px; }
    .ssp-outcomes-grid { grid-template-columns: repeat(2, 1fr); gap: 14px; }
    .ssp-overview-card { padding: 24px 22px; }
    .ssp-section-title { font-size: 1.35rem; margin-bottom: 28px; }
    .dp-inner { padding: 28px 20px 64px; }
    .dp-body { grid-template-columns: 1fr; gap: 20px; }
    .dp-header { flex-wrap: wrap; }
  }
  @media (max-width: 480px) {
    .ssp-outcomes-grid { grid-template-columns: 1fr 1fr; gap: 12px; }
    .ssp-outcome-card { padding: 22px 14px; }
    .dp-inner { padding: 20px 16px 56px; }
    .dp-icon-wrap { width: 50px; height: 50px; font-size: 1.4rem; }
    .ssp-overview-card { padding: 18px 16px; }
  }
`;

/* ─── Detail Panel Component ─── */
function DetailPanel({ cap, onClose }) {
  const panelRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    requestAnimationFrame(() => {
      if (panelRef.current) panelRef.current.classList.add("dp-open");
    });
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, []); // eslint-disable-line

  function handleClose() {
    if (panelRef.current) {
      panelRef.current.classList.remove("dp-open");
      setTimeout(onClose, 420);
    } else {
      onClose();
    }
  }

  return (
    <div className="dp-backdrop" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="dp-panel" ref={panelRef} role="dialog" aria-modal="true" aria-label={cap.title}>
        <div className="dp-handle" />
        <div className="dp-inner">

          {/* Header */}
          <div className="dp-header">
            <div className="dp-header-left">
              <div className="dp-icon-wrap">{cap.icon}</div>
              <div>
                <span className="dp-stag">Service Capability</span>
                <div className="dp-title">{cap.title}</div>
              </div>
            </div>
            <button className="dp-close" onClick={handleClose} aria-label="Close">✕</button>
          </div>

          {/* Description lead */}
          <div className="dp-desc">{cap.desc}</div>

          {/* Two-column body */}
          <div className="dp-body">
            {/* What We Do */}
            <div>
              <div className="dp-col-label">What We Deliver</div>
              {cap.whatWeDo.map((item, i) => (
                <div className="dp-item" key={i}>
                  <span className="dp-dot" />
                  {item}
                </div>
              ))}
            </div>
            {/* Why Different */}
            <div>
              <div className="dp-col-label">Why Inovaantage</div>
              {cap.whyDifferent.map((item, i) => (
                <div className="dp-diff" key={i}>
                  <span className="dp-star">✦</span>
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Value Delivered */}
          <div className="dp-value">
            <div className="dp-val-icon">🎯</div>
            <div>
              <div className="dp-val-lbl">Value Delivered</div>
              <div className="dp-val-txt">{cap.valueDelivered}</div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ─── Capability Card Component ─── */
function CapCard({ cap, index, onReadMore }) {
  return (
    <div className="cap-card">
      {/* <span className="cap-num">{String(index + 1).padStart(2, "0")}</span> */}
      <div className="cap-icon-box">{cap.icon}</div>
      <div className="cap-accent-bar" />
      <div className="cap-title">{cap.title}</div>
      <div className="cap-desc">{cap.desc}</div>
      <button className="cap-readmore" onClick={() => onReadMore(cap)}>
        Read More <span className="cap-arrow">→</span>
      </button>
    </div>
  );
}

/* ─── Main Export ─── */
export default function ServicesSubPage({ page }) {
  const data = serviceData[page];
  const [activeDetail, setActiveDetail] = useState(null);

  if (!data) return null;

  const words = data.title.split(" ");
  const lastWord = words[words.length - 1];
  const firstWords = words.slice(0, -1).join(" ");

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      <div className="ssp-root">

        {/* HERO */}
        <section className="ssp-hero">
          <div className="ssp-hero-grid" />
          <div className="ssp-hero-badge">
            <span>{data.icon}</span>
            Offerings → Services → {data.title}
          </div>
          <h1 className="ssp-hero-title">
            {firstWords && <>{firstWords} </>}
            <span className="accent-word">{lastWord}</span>
          </h1>
          <p className="ssp-hero-tagline">{data.tagline}</p>
        </section>

        {/* OVERVIEW */}
        <section className="ssp-section">
          <div className="ssp-container">
            <div className="ssp-overview-card">
              <h2>Overview</h2>
              <p>{data.overview}</p>
            </div>
          </div>
        </section>

        {/* CAPABILITIES */}
        <section className="ssp-section alt">
          <div className="ssp-container">
            <div className="ssp-section-title">Services We Deliver</div>
            <div className="ssp-cap-grid">
              {data.capabilities.map((cap, i) => (
                <CapCard key={i} cap={cap} index={i} onReadMore={setActiveDetail} />
              ))}
            </div>
          </div>
        </section>

        {/* OUTCOMES */}
        <section className="ssp-section">
          <div className="ssp-container">
            <div className="ssp-section-title">Measurable Outcomes</div>
            <div className="ssp-outcomes-grid">
              {data.outcomes.map((o, i) => (
                <div className="ssp-outcome-card" key={i}>
                  <span className="ssp-outcome-val">{o.val}</span>
                  <span className="ssp-outcome-label">{o.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TRUSTED BY */}
        <section className="ssp-section alt">
          <div className="ssp-container">
            <div className="ssp-section-title">Trusted Across Industries</div>
            <div className="ssp-clients">
              {data.clients.map((c, i) => (
                <div className="ssp-client-badge" key={i}>{c}</div>
              ))}
            </div>
          </div>
        </section>

      </div>

      {/* DETAIL PANEL */}
      {activeDetail && (
        <DetailPanel cap={activeDetail} onClose={() => setActiveDetail(null)} />
      )}
    </>
  );
}
